/**
 * One finite layout document for console forms, portal forms, pages and
 * dashboards (#1860 / ADR F-30).
 *
 * A layout arranges keys that another governed catalogue owns. It never names
 * React components, executable code, SQL, URLs or arbitrary HTML. The same
 * document can therefore be authored by the structured editor, Copilot or MCP
 * and pass through one validator before it reaches any host.
 *
 * `unplaced` is deliberate. Removing a field from a form is not deleting the
 * field, and a newly installed or retired dependency must not vanish because a
 * saved layout predates it. Reconciliation keeps every item and returns an
 * explicit conflict/status beside the document.
 */

import { isRecord as object } from './documentShape.js'

export const LAYOUT_SCHEMA_VERSION = 1 as const
/** The responsive-column grammar is accepted at persistence boundaries before
 * the hosts adopt it. V1 remains the active authoring version until that
 * separate renderer/composer slice lands. */
export const LAYOUT_SCHEMA_VERSION_V2 = 2 as const

export const LAYOUT_PROFILES = ['desktop', 'tablet', 'phone'] as const
export type LayoutProfile = typeof LAYOUT_PROFILES[number]

export interface ResponsiveColumns {
  desktop: 1 | 2 | 3 | 4
  tablet: 1 | 2 | 3 | 4
  phone: 1 | 2
}

export const LAYOUT_SURFACES = [
  'console_form', 'portal_form', 'page', 'dashboard',
] as const
export type LayoutSurface = typeof LAYOUT_SURFACES[number]

export const LAYOUT_ITEM_KINDS = ['field', 'relation', 'widget'] as const
export type LayoutItemKind = typeof LAYOUT_ITEM_KINDS[number]

export const LAYOUT_DEPENDENCY_KINDS = [
  'entity', 'field', 'relation', 'rule', 'widget', 'control',
] as const
export type LayoutDependencyKind = typeof LAYOUT_DEPENDENCY_KINDS[number]

export interface ResponsiveSpan {
  desktop: 1 | 2 | 3 | 4
  tablet: 1 | 2 | 3 | 4
  phone: 1
}

export interface ResponsiveSpanV2 {
  desktop: 1 | 2 | 3 | 4
  tablet: 1 | 2 | 3 | 4
  phone: 1 | 2
}

export interface LayoutRuleRef {
  /** Opaque rule-registry key. Never an expression evaluated in the browser. */
  rule: string
}

export interface LayoutItem {
  /** Stable component identity. Labels and positions may change without it. */
  id: string
  kind: LayoutItemKind
  /** Opaque catalogue key: a metamodel field, relationship or governed widget. */
  key: string
  span: ResponsiveSpan
  hidden?: boolean
  when?: LayoutRuleRef
}

export interface LayoutSection {
  /** Stable section identity; a rename must not orphan user overrides. */
  id: string
  label: string
  /** Optional governed helper copy shown below the section heading. */
  description?: string
  columns: 1 | 2 | 3 | 4
  items: LayoutItem[]
}

export interface LayoutItemV2 extends Omit<LayoutItem, 'span'> {
  span: ResponsiveSpanV2
}

export interface LayoutSectionV2 extends Omit<LayoutSection, 'columns' | 'items'> {
  columns: ResponsiveColumns
  items: LayoutItemV2[]
}

export interface LayoutDependency {
  kind: LayoutDependencyKind
  key: string
  /** A semantic version or immutable registry revision, as owned by the source. */
  version?: string
}

export interface LayoutDocument {
  schemaVersion: typeof LAYOUT_SCHEMA_VERSION
  /** Stable namespaced component id used by solution manifests and diffs. */
  id: string
  revision: number
  surface: LayoutSurface
  /** Entity key for a record form; absent for pages and dashboards. */
  entity?: string
  sections: LayoutSection[]
  /** Known components deliberately removed from the visible layout. */
  unplaced: LayoutItem[]
  dependencies: LayoutDependency[]
}

/** Persisted v2 shape. Kept parallel to LayoutDocument while current hosts
 * still render v1; accepting a document is not permission for an old renderer
 * to silently reinterpret it. */
export interface LayoutDocumentV2 extends Omit<LayoutDocument, 'schemaVersion' | 'sections' | 'unplaced'> {
  schemaVersion: typeof LAYOUT_SCHEMA_VERSION_V2
  sections: LayoutSectionV2[]
  unplaced: LayoutItemV2[]
}

export type PersistedLayoutDocument = LayoutDocument | LayoutDocumentV2

export interface LayoutCatalogItem {
  id: string
  kind: LayoutItemKind
  key: string
  required?: boolean
  /** Where a newly installed item belongs when the base document knows it. */
  sectionId?: string
}

export interface LayoutCatalog {
  items: readonly LayoutCatalogItem[]
  dependencies?: readonly LayoutDependency[]
}

export type LayoutConflictKind =
  | 'dependency_missing'
  | 'dependency_version_mismatch'
  | 'item_missing'
  | 'required_item_unplaced'

export interface LayoutConflict {
  kind: LayoutConflictKind
  key: string
  message: string
}

export type LayoutReconciliationStatus = 'unchanged' | 'reconciled' | 'conflicted'

export interface LayoutReconciliation<D extends PersistedLayoutDocument = LayoutDocument> {
  document: D
  status: LayoutReconciliationStatus
  conflicts: LayoutConflict[]
  added: string[]
}

/**
 * The editor and an AI author both use these operations. Keeping them here
 * means a pointer drag, a keyboard move and an MCP-authored edit cannot invent
 * three subtly different document mutations.
 *
 * Draft operations deliberately retain `revision`. A revision identifies a
 * published document, not every keystroke in an editor. `publishLayoutRevision`
 * is the single boundary that validates the finished draft and increments it.
 */
export class LayoutEditError extends Error {
  override name = 'LayoutEditError'
}

export interface NewLayoutSection {
  id: string
  label: string
  /** A scalar is still accepted; it is shaped to the receiving document's own
   *  grammar by `addLayoutSection`, never stored as given. */
  columns?: 1 | 2 | 3 | 4 | ResponsiveColumns
}

export interface LayoutItemLocation {
  sectionId: string | null
  index: number
}

export type LayoutDiffKind = 'added' | 'removed' | 'moved' | 'changed'
export interface LayoutDiff {
  kind: LayoutDiffKind
  target: 'document' | 'section' | 'item' | 'dependency'
  id: string
  from?: string
  to?: string
}

const IDENTIFIER = /^[a-z][a-z0-9]*(?:[._-][a-z0-9]+)*$/
const VERSION = /^[0-9]+(?:\.[0-9]+){0,2}(?:[-+][a-z0-9.-]+)?$/i
const SURFACES = new Set<string>(LAYOUT_SURFACES)
const ITEM_KINDS = new Set<string>(LAYOUT_ITEM_KINDS)
const DEPENDENCY_KINDS = new Set<string>(LAYOUT_DEPENDENCY_KINDS)

const unknownKeys = (value: Record<string, unknown>, allowed: readonly string[], at: string): string[] => {
  const set = new Set(allowed)
  return Object.keys(value).filter((key) => !set.has(key))
    .map((key) => `${at}.${key} is not part of the finite grammar`)
}

function validateSpan(value: unknown, at: string, schemaVersion: 1 | 2): string[] {
  if (!object(value)) return [`${at} must be a responsive span`]
  const issues = unknownKeys(value, ['desktop', 'tablet', 'phone'], at)
  for (const viewport of ['desktop', 'tablet'] as const) {
    if (!Number.isInteger(value[viewport]) || Number(value[viewport]) < 1 || Number(value[viewport]) > 4) {
      issues.push(`${at}.${viewport} must be an integer from 1 to 4`)
    }
  }
  if (schemaVersion === 1) {
    if (value.phone !== 1) issues.push(`${at}.phone must be 1`)
  } else if (!Number.isInteger(value.phone) || Number(value.phone) < 1 || Number(value.phone) > 2) {
    issues.push(`${at}.phone must be an integer from 1 to 2`)
  }
  return issues
}

function validateItem(value: unknown, at: string, schemaVersion: 1 | 2): string[] {
  if (!object(value)) return [`${at} must be an item`]
  const issues = unknownKeys(value, ['id', 'kind', 'key', 'span', 'hidden', 'when'], at)
  if (typeof value.id !== 'string' || !IDENTIFIER.test(value.id)) issues.push(`${at}.id must be a namespaced identifier`)
  if (typeof value.kind !== 'string' || !ITEM_KINDS.has(value.kind)) issues.push(`${at}.kind is not admitted`)
  if (typeof value.key !== 'string' || !IDENTIFIER.test(value.key)) issues.push(`${at}.key must be an opaque identifier`)
  issues.push(...validateSpan(value.span, `${at}.span`, schemaVersion))
  if (value.hidden !== undefined && typeof value.hidden !== 'boolean') issues.push(`${at}.hidden must be boolean`)
  if (value.when !== undefined) {
    if (!object(value.when)) issues.push(`${at}.when must be a governed rule reference`)
    else {
      issues.push(...unknownKeys(value.when, ['rule'], `${at}.when`))
      if (typeof value.when.rule !== 'string' || !IDENTIFIER.test(value.when.rule)) {
        issues.push(`${at}.when.rule must be an opaque rule identifier`)
      }
    }
  }
  return issues
}

/** Strict runtime validation for persisted, imported or AI-authored documents. */
export function validateLayoutDocument(value: unknown, catalog?: LayoutCatalog): string[] {
  if (!object(value)) return ['document must be an object']
  const issues = unknownKeys(value, [
    'schemaVersion', 'id', 'revision', 'surface', 'entity', 'sections', 'unplaced', 'dependencies',
  ], 'document')
  const schemaVersion = value.schemaVersion === LAYOUT_SCHEMA_VERSION_V2 ? 2 : 1
  if (value.schemaVersion !== LAYOUT_SCHEMA_VERSION && value.schemaVersion !== LAYOUT_SCHEMA_VERSION_V2) {
    issues.push(`schemaVersion must be ${LAYOUT_SCHEMA_VERSION} or ${LAYOUT_SCHEMA_VERSION_V2}`)
  }
  if (typeof value.id !== 'string' || !IDENTIFIER.test(value.id)) issues.push('document.id must be a namespaced identifier')
  if (!Number.isSafeInteger(value.revision) || Number(value.revision) < 1) issues.push('document.revision must be a positive integer')
  if (typeof value.surface !== 'string' || !SURFACES.has(value.surface)) issues.push('document.surface is not admitted')
  if (value.entity !== undefined && (typeof value.entity !== 'string' || !IDENTIFIER.test(value.entity))) {
    issues.push('document.entity must be an opaque entity key')
  }
  if ((value.surface === 'console_form' || value.surface === 'portal_form') && typeof value.entity !== 'string') {
    issues.push('record-form documents must name an entity')
  }

  const seenSections = new Set<string>()
  const seenItems = new Set<string>()
  const seenFields = new Set<string>()
  const inspectItem = (item: unknown, at: string) => {
    issues.push(...validateItem(item, at, schemaVersion))
    if (!object(item) || typeof item.id !== 'string') return
    if (seenItems.has(item.id)) issues.push(`item id ${item.id} is duplicated`)
    else seenItems.add(item.id)
    if (item.kind === 'field' && typeof item.key === 'string') {
      if (seenFields.has(item.key)) issues.push(`field ${item.key} is placed more than once`)
      else seenFields.add(item.key)
    }
  }

  if (!Array.isArray(value.sections)) issues.push('document.sections must be an array')
  else value.sections.forEach((section, index) => {
    const at = `document.sections[${index}]`
    if (!object(section)) { issues.push(`${at} must be a section`); return }
    issues.push(...unknownKeys(section, ['id', 'label', 'description', 'columns', 'items'], at))
    if (typeof section.id !== 'string' || !IDENTIFIER.test(section.id)) issues.push(`${at}.id must be a namespaced identifier`)
    else if (seenSections.has(section.id)) issues.push(`section id ${section.id} is duplicated`)
    else seenSections.add(section.id)
    if (typeof section.label !== 'string' || !section.label.trim()) issues.push(`${at}.label must not be blank`)
    if (section.description !== undefined && typeof section.description !== 'string') {
      issues.push(`${at}.description must be text`)
    }
    if (schemaVersion === 1) {
      if (!Number.isInteger(section.columns) || Number(section.columns) < 1 || Number(section.columns) > 4) {
        issues.push(`${at}.columns must be an integer from 1 to 4`)
      }
    } else if (!object(section.columns)) {
      issues.push(`${at}.columns must be responsive columns`)
    } else {
      issues.push(...unknownKeys(section.columns, LAYOUT_PROFILES, `${at}.columns`))
      for (const profile of ['desktop', 'tablet'] as const) {
        if (!Number.isInteger(section.columns[profile]) || Number(section.columns[profile]) < 1 || Number(section.columns[profile]) > 4) {
          issues.push(`${at}.columns.${profile} must be an integer from 1 to 4`)
        }
      }
      if (!Number.isInteger(section.columns.phone) || Number(section.columns.phone) < 1 || Number(section.columns.phone) > 2) {
        issues.push(`${at}.columns.phone must be an integer from 1 to 2`)
      }
    }
    if (!Array.isArray(section.items)) issues.push(`${at}.items must be an array`)
    else section.items.forEach((item, itemIndex) => {
      inspectItem(item, `${at}.items[${itemIndex}]`)
      if (!object(item) || !object(item.span)) return
      for (const profile of LAYOUT_PROFILES) {
        const columns = schemaVersion === 1
          ? (Number.isInteger(section.columns) ? Number(section.columns) : null)
          : (object(section.columns) && typeof section.columns[profile] === 'number'
              ? Number(section.columns[profile]) : null)
        if (columns != null && typeof item.span[profile] === 'number' && item.span[profile] > columns) {
          issues.push(`${at}.items[${itemIndex}].span.${profile} cannot exceed its section columns`)
        }
      }
    })
  })

  if (!Array.isArray(value.unplaced)) issues.push('document.unplaced must be an array')
  else value.unplaced.forEach((item, index) => inspectItem(item, `document.unplaced[${index}]`))

  const dependencies = new Set<string>()
  if (!Array.isArray(value.dependencies)) issues.push('document.dependencies must be an array')
  else value.dependencies.forEach((dependency, index) => {
    const at = `document.dependencies[${index}]`
    if (!object(dependency)) { issues.push(`${at} must be a dependency`); return }
    issues.push(...unknownKeys(dependency, ['kind', 'key', 'version'], at))
    if (typeof dependency.kind !== 'string' || !DEPENDENCY_KINDS.has(dependency.kind)) issues.push(`${at}.kind is not admitted`)
    if (typeof dependency.key !== 'string' || !IDENTIFIER.test(dependency.key)) issues.push(`${at}.key must be an opaque identifier`)
    if (dependency.version !== undefined && (typeof dependency.version !== 'string' || !VERSION.test(dependency.version))) {
      issues.push(`${at}.version must be a version string`)
    }
    const identity = `${String(dependency.kind)}:${String(dependency.key)}`
    if (dependencies.has(identity)) issues.push(`dependency ${identity} is duplicated`)
    else dependencies.add(identity)
  })

  if (catalog) {
    const byId = new Map(catalog.items.map((item) => [item.id, item]))
    for (const id of seenItems) if (!byId.has(id)) issues.push(`item ${id} is not in the governed catalogue`)
    const unplaced = new Set(Array.isArray(value.unplaced)
      ? value.unplaced.flatMap((item) => object(item) && typeof item.id === 'string' ? [item.id] : [])
      : [])
    const items = new Map([
      ...(Array.isArray(value.sections) ? value.sections.flatMap((section) => object(section) && Array.isArray(section.items) ? section.items : []) : []),
      ...(Array.isArray(value.unplaced) ? value.unplaced : []),
    ].flatMap((item) => object(item) && typeof item.id === 'string' ? [[item.id, item] as const] : []))
    for (const item of catalog.items) {
      if (item.required && (unplaced.has(item.id) || items.get(item.id)?.hidden === true)) {
        issues.push(`required item ${item.id} cannot be hidden or unplaced`)
      }
      if (item.required && !seenItems.has(item.id)) issues.push(`required item ${item.id} is missing`)
    }
  }
  return issues
}

/** Stable JSON for solution artefacts. Section and item order stays meaningful. */
export function canonicalLayoutDocument(document: PersistedLayoutDocument): string {
  const dependencies = [...document.dependencies].sort((a, b) =>
    `${a.kind}:${a.key}:${a.version ?? ''}`.localeCompare(`${b.kind}:${b.key}:${b.version ?? ''}`))
  return JSON.stringify({ ...document, dependencies })
}

/**
 * Read either persisted grammar into one in-memory v2 document. This is a pure
 * adapter: it never rewrites the source or the stored experience row.
 */
export function normalizeLayoutDocument(value: unknown): LayoutDocumentV2 {
  const issues = validateLayoutDocument(value)
  if (issues.length) throw new LayoutEditError(issues.join('; '))
  const source = structuredClone(value) as PersistedLayoutDocument
  if (source.schemaVersion === LAYOUT_SCHEMA_VERSION_V2) return source
  return {
    ...source,
    schemaVersion: LAYOUT_SCHEMA_VERSION_V2,
    sections: source.sections.map((section): LayoutSectionV2 => {
      const tabletColumns = Math.min(section.columns, 2) as 1 | 2
      return {
        ...section,
        columns: { desktop: section.columns, tablet: tabletColumns, phone: 1 },
        items: section.items.map((item): LayoutItemV2 => ({
          ...item,
          span: {
            ...item.span,
            tablet: Math.min(item.span.tablet, tabletColumns) as 1 | 2,
          },
        })),
      }
    }),
    unplaced: source.unplaced.map((item): LayoutItemV2 => ({ ...item, span: { ...item.span } })),
  }
}

const profileMaximum = (profile: LayoutProfile): 2 | 4 => profile === 'phone' ? 2 : 4

const assertProfileValue = (profile: LayoutProfile, value: number, noun: string): void => {
  const maximum = profileMaximum(profile)
  if (!Number.isInteger(value) || value < 1 || value > maximum) {
    throw new LayoutEditError(`${noun}.${profile} must be an integer from 1 to ${maximum}`)
  }
}

/** Change one responsive track without mutating either other profile. Items on
 * that track are clamped in the same immutable operation. */
export function setLayoutSectionProfileColumns(
  document: LayoutDocumentV2,
  id: string,
  profile: LayoutProfile,
  columns: number,
): LayoutDocumentV2 {
  assertProfileValue(profile, columns, 'section columns')
  const next = structuredClone(document)
  const section = next.sections.find((candidate) => candidate.id === id)
  if (!section) throw new LayoutEditError(`layout section ${id} does not exist`)
  section.columns = { ...section.columns, [profile]: columns } as ResponsiveColumns
  section.items.forEach((item) => {
    item.span = {
      ...item.span,
      [profile]: Math.min(item.span[profile], columns),
    } as ResponsiveSpanV2
  })
  return next
}

/** Change one item's span on one profile. A placed item cannot be authored
 * wider than its section; an unplaced item retains any globally valid span. */
export function setLayoutItemProfileSpan(
  document: LayoutDocumentV2,
  id: string,
  profile: LayoutProfile,
  span: number,
): LayoutDocumentV2 {
  assertProfileValue(profile, span, 'span')
  const next = structuredClone(document)
  let item = next.unplaced.find((candidate) => candidate.id === id)
  let sectionColumns: number | null = null
  for (const section of next.sections) {
    const candidate = section.items.find((entry) => entry.id === id)
    if (!candidate) continue
    item = candidate
    sectionColumns = section.columns[profile]
    break
  }
  if (!item) throw new LayoutEditError(`layout item ${id} does not exist`)
  if (sectionColumns != null && span > sectionColumns) {
    throw new LayoutEditError(`span.${profile} cannot exceed its section columns`)
  }
  item.span = { ...item.span, [profile]: span } as ResponsiveSpanV2
  return next
}

const itemPath = (document: LayoutDocument): Map<string, string> => {
  const paths = new Map<string, string>()
  document.sections.forEach((section, sectionIndex) => section.items.forEach((item, itemIndex) =>
    paths.set(item.id, `sections/${sectionIndex}:${section.id}/items/${itemIndex}`)))
  document.unplaced.forEach((item, index) => paths.set(item.id, `unplaced/${index}`))
  return paths
}

/** Semantic rather than byte diff: ordering and placement remain first-class. */
export function diffLayoutDocuments(before: LayoutDocument, after: LayoutDocument): LayoutDiff[] {
  const out: LayoutDiff[] = []
  for (const key of ['surface', 'entity'] as const) {
    if (before[key] !== after[key]) out.push({ kind: 'changed', target: 'document', id: key, from: String(before[key] ?? ''), to: String(after[key] ?? '') })
  }
  const beforeSections = new Map(before.sections.map((section, index) => [section.id, { section, index }]))
  const afterSections = new Map(after.sections.map((section, index) => [section.id, { section, index }]))
  for (const [id, old] of beforeSections) {
    const next = afterSections.get(id)
    if (!next) out.push({ kind: 'removed', target: 'section', id })
    else {
      if (old.index !== next.index) out.push({ kind: 'moved', target: 'section', id, from: String(old.index), to: String(next.index) })
      if (old.section.label !== next.section.label || old.section.columns !== next.section.columns) {
        out.push({ kind: 'changed', target: 'section', id })
      }
    }
  }
  for (const id of afterSections.keys()) if (!beforeSections.has(id)) out.push({ kind: 'added', target: 'section', id })

  const beforePaths = itemPath(before)
  const afterPaths = itemPath(after)
  const beforeItems = new Map([...before.sections.flatMap((section) => section.items), ...before.unplaced].map((item) => [item.id, item]))
  const afterItems = new Map([...after.sections.flatMap((section) => section.items), ...after.unplaced].map((item) => [item.id, item]))
  for (const [id, old] of beforeItems) {
    const next = afterItems.get(id)
    if (!next) out.push({ kind: 'removed', target: 'item', id })
    else {
      if (beforePaths.get(id) !== afterPaths.get(id)) out.push({ kind: 'moved', target: 'item', id, from: beforePaths.get(id), to: afterPaths.get(id) })
      if (canonicalItem(old) !== canonicalItem(next)) out.push({ kind: 'changed', target: 'item', id })
    }
  }
  for (const id of afterItems.keys()) if (!beforeItems.has(id)) out.push({ kind: 'added', target: 'item', id })

  const beforeDeps = new Map(before.dependencies.map((dependency) => [`${dependency.kind}:${dependency.key}`, dependency.version ?? '']))
  const afterDeps = new Map(after.dependencies.map((dependency) => [`${dependency.kind}:${dependency.key}`, dependency.version ?? '']))
  for (const [id, version] of beforeDeps) {
    if (!afterDeps.has(id)) out.push({ kind: 'removed', target: 'dependency', id })
    else if (afterDeps.get(id) !== version) out.push({ kind: 'changed', target: 'dependency', id, from: version, to: afterDeps.get(id) })
  }
  for (const id of afterDeps.keys()) if (!beforeDeps.has(id)) out.push({ kind: 'added', target: 'dependency', id })
  return out
}

const canonicalItem = (item: LayoutItem) => JSON.stringify(item)

const draft = (document: LayoutDocument): LayoutDocument => structuredClone(document)

/**
 * Every operation below this line moves sections and items between positions.
 * None of them reads or writes geometry, so a v1 and a v2 document travel
 * through them unchanged — which is why they are typed generically for callers
 * and share one structural view internally.
 *
 * The cast is the whole reason this is written down: it is sound only while
 * those operations stay geometry-blind. `addLayoutSection` is the single
 * exception, and shapes columns explicitly at the point it creates them.
 */
const structural = (document: PersistedLayoutDocument): LayoutDocument =>
  document as unknown as LayoutDocument

/**
 * A new section takes the geometry shape of the document it joins.
 *
 * A v2 document gets `ResponsiveColumns`, derived from a scalar by the SAME
 * rule `normalizeLayoutDocument` applies on read — so a section added here and
 * a v1 section normalized on read are indistinguishable afterwards. Two rules
 * for one derivation is exactly how the two hosts drifted apart before.
 */
const sectionColumnsFor = (
  document: PersistedLayoutDocument,
  requested: NewLayoutSection['columns'],
): number | ResponsiveColumns => {
  if (document.schemaVersion !== LAYOUT_SCHEMA_VERSION_V2) {
    return typeof requested === 'object' ? requested.desktop : requested ?? 1
  }
  if (typeof requested === 'object') return requested
  const columns = requested ?? 1
  return { desktop: columns, tablet: Math.min(columns, 2), phone: 1 } as ResponsiveColumns
}

const sectionIndex = (document: LayoutDocument, id: string): number => {
  const index = document.sections.findIndex((section) => section.id === id)
  if (index < 0) throw new LayoutEditError(`layout section ${id} does not exist`)
  return index
}

const itemLocation = (document: LayoutDocument, id: string): LayoutItemLocation => {
  for (const section of document.sections) {
    const index = section.items.findIndex((item) => item.id === id)
    if (index >= 0) return { sectionId: section.id, index }
  }
  const index = document.unplaced.findIndex((item) => item.id === id)
  if (index >= 0) return { sectionId: null, index }
  throw new LayoutEditError(`layout item ${id} does not exist`)
}

const takeItem = (document: LayoutDocument, id: string): LayoutItem => {
  const location = itemLocation(document, id)
  if (location.sectionId === null) return document.unplaced.splice(location.index, 1)[0]!
  const section = document.sections[sectionIndex(document, location.sectionId)]!
  return section.items.splice(location.index, 1)[0]!
}

/** Add a stable section. A label may change; the id is its ALM identity. */
export function addLayoutSection<D extends PersistedLayoutDocument>(
  document: D, section: NewLayoutSection, at = document.sections.length,
): D {
  if (!IDENTIFIER.test(section.id)) throw new LayoutEditError('section id must be a namespaced identifier')
  if (!section.label.trim()) throw new LayoutEditError('section label must not be blank')
  if (document.sections.some((candidate) => candidate.id === section.id)) {
    throw new LayoutEditError(`layout section ${section.id} already exists`)
  }
  if (!Number.isInteger(at) || at < 0 || at > document.sections.length) {
    throw new LayoutEditError('section position is outside the layout')
  }
  const next = draft(structural(document))
  next.sections.splice(at, 0, {
    id: section.id,
    label: section.label.trim(),
    columns: sectionColumnsFor(document, section.columns) as LayoutSection['columns'],
    items: [],
  })
  return next as unknown as D
}

export function renameLayoutSection<D extends PersistedLayoutDocument>(document: D, id: string, label: string): D {
  if (!label.trim()) throw new LayoutEditError('section label must not be blank')
  const next = draft(structural(document))
  next.sections[sectionIndex(next, id)]!.label = label.trim()
  return next as unknown as D
}

export function setLayoutSectionColumns(
  document: LayoutDocument,
  id: string,
  columns: 1 | 2 | 3 | 4,
): LayoutDocument {
  if (![1, 2, 3, 4].includes(columns)) throw new LayoutEditError('section columns must be from 1 to 4')
  const next = draft(document)
  const section = next.sections[sectionIndex(next, id)]!
  section.columns = columns
  section.items = section.items.map((item) => ({
    ...item,
    span: {
      ...item.span,
      desktop: Math.min(item.span.desktop, columns) as 1 | 2 | 3 | 4,
      tablet: Math.min(item.span.tablet, columns) as 1 | 2 | 3 | 4,
    },
  }))
  return next
}

export function moveLayoutSection<D extends PersistedLayoutDocument>(document: D, id: string, to: number): D {
  if (!Number.isInteger(to) || to < 0 || to >= document.sections.length) {
    throw new LayoutEditError('section position is outside the layout')
  }
  const next = draft(structural(document))
  const from = sectionIndex(next, id)
  const section = next.sections.splice(from, 1)[0]!
  next.sections.splice(to, 0, section)
  return next as unknown as D
}

/** Split one section before the item at `at`. The new section is inserted
 * immediately after its source and takes the trailing items without cloning or
 * losing their stable identities. */
export function splitLayoutSection<D extends PersistedLayoutDocument>(
  document: D,
  id: string,
  at: number,
  section: NewLayoutSection,
): D {
  const sourceAt = sectionIndex(structural(document), id)
  const source = structural(document).sections[sourceAt]!
  if (!Number.isInteger(at) || at <= 0 || at >= source.items.length) {
    throw new LayoutEditError('a section split must leave at least one item on each side')
  }
  const next = structural(addLayoutSection(document, section, sourceAt + 1))
  const liveSource = next.sections[sourceAt]!
  next.sections[sourceAt + 1]!.items = liveSource.items.splice(at)
  return next as unknown as D
}

/** Merge a section into another one and remove only the now-empty shell. */
export function mergeLayoutSections<D extends PersistedLayoutDocument>(document: D, sourceId: string, targetId: string): D {
  if (sourceId === targetId) throw new LayoutEditError('a section cannot be merged into itself')
  const next = draft(structural(document))
  const sourceAt = sectionIndex(next, sourceId)
  const targetAt = sectionIndex(next, targetId)
  const source = next.sections[sourceAt]!
  next.sections[targetAt]!.items.push(...source.items)
  next.sections.splice(sourceAt, 1)
  return next as unknown as D
}

/** Move a field/relation/widget between sections or restore an unplaced item. */
export function placeLayoutItem<D extends PersistedLayoutDocument>(
  document: D,
  id: string,
  targetSectionId: string,
  at?: number,
): D {
  const next = draft(structural(document))
  const target = next.sections[sectionIndex(next, targetSectionId)]!
  const item = takeItem(next, id)
  const position = at ?? target.items.length
  if (!Number.isInteger(position) || position < 0 || position > target.items.length) {
    throw new LayoutEditError('item position is outside the section')
  }
  target.items.splice(position, 0, item)
  return next as unknown as D
}

/**
 * Remove from layout, never from the schema. Required catalogue items are
 * refused here, before a host can accidentally offer a destructive-looking UI.
 */
export function unplaceLayoutItem<D extends PersistedLayoutDocument>(document: D, id: string, catalog?: LayoutCatalog): D {
  if (catalog?.items.find((item) => item.id === id)?.required) {
    throw new LayoutEditError(`required item ${id} cannot be unplaced`)
  }
  const next = draft(structural(document))
  const item = takeItem(next, id)
  next.unplaced.push(item)
  return next as unknown as D
}

/**
 * Removing a section keeps every child as explicitly unplaced. Silent field
 * deletion is not an editor operation and cannot be expressed through this API.
 */
export function removeLayoutSection<D extends PersistedLayoutDocument>(document: D, id: string): D {
  const next = draft(structural(document))
  const index = sectionIndex(next, id)
  const removed = next.sections.splice(index, 1)[0]!
  next.unplaced.push(...removed.items)
  return next as unknown as D
}

export function setLayoutItemSpan(document: LayoutDocument, id: string, span: ResponsiveSpan): LayoutDocument {
  const issues = validateSpan(span, 'span', 1)
  if (issues.length) throw new LayoutEditError(issues.join('; '))
  const next = draft(document)
  const location = itemLocation(next, id)
  const item = location.sectionId === null
    ? next.unplaced[location.index]!
    : next.sections[sectionIndex(next, location.sectionId)]!.items[location.index]!
  item.span = { ...span }
  return next
}

export function setLayoutItemHidden<D extends PersistedLayoutDocument>(document: D, id: string, hidden: boolean): D {
  const next = draft(structural(document))
  const location = itemLocation(next, id)
  const item = location.sectionId === null
    ? next.unplaced[location.index]!
    : next.sections[sectionIndex(next, location.sectionId)]!.items[location.index]!
  if (hidden) item.hidden = true
  else delete item.hidden
  return next as unknown as D
}

/** Validate and mint exactly one new immutable revision at the save boundary. */
export function publishLayoutRevision<D extends PersistedLayoutDocument>(document: D, catalog?: LayoutCatalog): D {
  const issues = validateLayoutDocument(document, catalog)
  if (issues.length) throw new LayoutEditError(issues.join('; '))
  return { ...draft(structural(document)), revision: document.revision + 1 } as unknown as D
}

/**
 * Reconcile a saved document against the current governed catalogue and the
 * current shipped base. Nothing disappears: missing dependencies/items stay in
 * the document and become conflicts; newly installed items are appended to the
 * base section that owns them, or to `unplaced` when no section is declared.
 */
export function reconcileLayoutDocument<D extends PersistedLayoutDocument>(
  authored: D,
  base: D,
  catalog: LayoutCatalog,
): LayoutReconciliation<D> {
  /* Reconciliation adds, removes and re-places WHOLE items. It never reads a
     span or a column count, which is why one authored grammar in gives the
     same grammar out — and why `base` must already be in that same grammar:
     items are copied from it verbatim, so a v1 base would post v1-shaped items
     into a v2 document. `resolveFormLayout` normalizes both before calling. */
  const next = structuredClone(structural(authored))
  const conflicts: LayoutConflict[] = []
  const added: string[] = []
  const catalogById = new Map(catalog.items.map((item) => [item.id, item]))
  const present = new Set([...next.sections.flatMap((section) => section.items), ...next.unplaced].map((item) => item.id))
  const baseline = structural(base)
  const baseItems = new Map([...baseline.sections.flatMap((section) => section.items), ...baseline.unplaced].map((item) => [item.id, item]))

  for (const item of [...next.sections.flatMap((section) => section.items), ...next.unplaced]) {
    const current = catalogById.get(item.id)
    if (!current || current.kind !== item.kind || current.key !== item.key) {
      conflicts.push({ kind: 'item_missing', key: item.id, message: `${item.id} is not available in the current catalogue` })
    }
  }

  for (const current of catalog.items) {
    if (present.has(current.id)) continue
    const baseItem = baseItems.get(current.id)
    if (!baseItem) {
      conflicts.push({ kind: 'item_missing', key: current.id, message: `${current.id} is available but has no shipped placement` })
      continue
    }
    const targetId = current.sectionId
      ?? baseline.sections.find((section) => section.items.some((item) => item.id === current.id))?.id
    const target = targetId ? next.sections.find((section) => section.id === targetId) : undefined
    if (target) target.items.push(structuredClone(baseItem))
    else next.unplaced.push(structuredClone(baseItem))
    present.add(current.id)
    added.push(current.id)
  }

  const unplaced = new Set(next.unplaced.map((item) => item.id))
  for (const item of catalog.items) if (item.required && unplaced.has(item.id)) {
    conflicts.push({ kind: 'required_item_unplaced', key: item.id, message: `${item.id} is required and cannot remain unplaced` })
  }

  const availableDependencies = new Map((catalog.dependencies ?? []).map((dependency) => [
    `${dependency.kind}:${dependency.key}`, dependency.version,
  ]))
  for (const dependency of next.dependencies) {
    const key = `${dependency.kind}:${dependency.key}`
    if (!availableDependencies.has(key)) {
      conflicts.push({ kind: 'dependency_missing', key, message: `${key} is not available in this environment` })
    } else if (dependency.version && availableDependencies.get(key) !== dependency.version) {
      conflicts.push({
        kind: 'dependency_version_mismatch', key,
        message: `${key} requires ${dependency.version}; this environment has ${availableDependencies.get(key) ?? 'no version'}`,
      })
    }
  }

  return {
    document: next as unknown as D,
    status: conflicts.length ? 'conflicted' : added.length ? 'reconciled' : 'unchanged',
    conflicts,
    added,
  }
}
